<script>
/** Type mighty be : default (or null), highlight, overlay**/
export default {
  props: {
    click: Function,
    label: String,
    pending: Boolean,
    type: [Array, String],
    href: String,
    blank: Boolean,
  },
};
</script>

<template>
  <a
    class="keepfocus f-button f-button-link"
    :target="blank ? '_blank' : ''"
    :href="href"
    :class="[type, { pending }]"
  >
    <slot></slot>
    <i class="fa fa-circle-o-notch fa-spin fa-fw icon-pending"></i>
  </a>
</template>

<style lang="scss">
a.f-button {
  outline: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 11px 22px;
  border: none;
  text-transform: uppercase;
  font-size: 14px;
  border-radius: 6px;
  font-family: Lato;
  font-weight: bold;
  position: relative;
  gap: 10px;
  text-align: center;

  &:focus {
    outline-offset: inherit; // #2987
  }

  &:hover
  {
    text-decoration: none;
  }

  &.small {
    font-size: 12px;
    padding: 0 16px;
    height: 40px;
    font-weight: 700;
  }

  /** Primary **/

  &.primary {
    background: var(--turquoise);
    border: 2px solid var(--turquoise);
    color: white;

    &:hover,
    &:focus {
      border: 2px solid var(--turquoise-d90);
      background-color: var(--turquoise-d90);
    }

    &.active,
    &:active,
    &:active:hover {
      border: 2px solid var(--turquoise-d80);
      background-color: var(--turquoise-d80);
    }

    i.fa.fa-circle-o-notch {
      color: white;
    }
  }

  &.primary.overlay {
    background: var(--white);
    border: 2px solid var(--white);
    color: var(--black);

    &:hover,
    &:focus {
      background: var(--black-l10);
      border: 2px solid var(--black-l10);
    }

    &.active,
    &:active,
    &:active:hover {
      background: var(--black-l20);
      border: 2px solid var(--black-l20);
    }

    i.fa.fa-circle-o-notch {
      color: #161615;
    }
  }

  /** Secondary **/

  &.secondary {
    background: transparent;
    color: var(--blue);
    border: 2px solid var(--blue-l75);

    &:hover,
    &:focus {
      background-color: var(--blue-l10);
    }

    &.active,
    &:active,
    &:active:hover {
      background-color: var(--blue-l20);
    }

    i.fa.fa-circle-o-notch {
      color: #1890d3;
    }
  }

  &.secondary.overlay {
    background: transparent;
    color: var(--white);
    border: 2px solid var(--white);

    &:hover,
    &:focus {
      background-color: var(--white-a10);
    }

    &.active,
    &:active,
    &:active:hover {
      background-color: var(--white-a25);
    }

    i.fa.fa-circle-o-notch {
      color: #ffffff;
    }
  }

  &.secondary.negative {
    background: transparent;
    color: var(--statusNegative);
    border: 2px solid var(--statusNegative-a50);

    &:hover,
    &:focus {
      background: var(--statusNegative-a5);
      border: 2px solid var(--statusNegative-a50);
    }

    &.active,
    &:active,
    &:active:hover {
      background: var(--statusNegative-a10);
      border: 2px solid var(--statusNegative-a50);
    }

    i.fa.fa-circle-o-notch {
      color: #ffffff;
    }
  }

  /** Tertiary **/

  &.tertiary {
    background: transparent;
    color: #1890d3;
    text-decoration: none;
    border: 2px solid transparent;

    &:hover,
    &:focus {
      background: var(--interactiveBlueHover);
      border: 2px solid transparent;
    }

    &.active,
    &:active,
    &:active:hover {
      background: var(--interactiveBluePress);
      border: 2px solid transparent;
    }

    i.fa.fa-circle-o-notch {
      color: #1890d3;
    }
  }

  &.tertiary.overlay {
    background: transparent;
    color: var(--white);
    border: 2px solid transparent;

    &:hover,
    &:focus {
      background-color: var(--white-a10);
      border: 2px solid transparent;
    }

    &.active,
    &:active,
    &:active:hover {
      background-color: var(--white-a25);
      border: 2px solid transparent;
    }

    i.fa.fa-circle-o-notch {
      color: #ffffff;
    }
  }

  &.tertiary.negative {
    background: transparent;
    color: var(--statusNegative);
    border: 2px solid transparent;

    &:hover,
    &:focus {
      background: var(--statusNegative-a5);
      border: 2px solid transparent;
    }

    &.active,
    &:active,
    &:active:hover {
      background: var(--statusNegative-a10);
      border: 2px solid transparent;
    }

    i.fa.fa-circle-o-notch {
      color: #ffffff;
    }
  }

  /** Highlight **/

  &.highlight {
    background: var(--yellow);
    border: 2px solid var(--yellow);
    color: var(--black);

    &:hover,
    &:focus {
      background: var(--yellow-d90);
      border: 2px solid var(--yellow-d90);
    }

    &.active,
    &:active,
    &:active:hover {
      background: var(--yellow-d80);
      border: 2px solid var(--yellow-d80);
    }

    i.fa.fa-circle-o-notch {
      color: #161615;
    }
  }
  /** Negative **/

  &.negative {
    background: var(--statusNegative);
    border: 2px solid var(--statusNegative);
    color: var(--white);

    &:hover,
    &:focus {
      background: var(--interactiveStatusNegativeHover);
      border: 2px solid var(--interactiveStatusNegativeHover);
    }

    &.active,
    &:active,
    &:active:hover {
      background: var(--interactiveStatusNegativePress);
      border: 2px solid var(--interactiveStatusNegativePress);
    }

    i.fa.fa-circle-o-notch {
      color: var(--white);
    }
  }

  /** Highlight **/

  &.danger {
    background: var(--statusNegative);
    border: 2px solid var(--statusNegative);
    color: var(--white);

    &:hover,
    &:focus {
      background: var(--interactiveStatusNegativeHover);
      border: 2px solid var(--interactiveStatusNegativeHover);
    }

    &.active,
    &:active,
    &:active:hover {
      background: var(--interactiveStatusNegativePress);
      border: 2px solid var(--interactiveStatusNegativePress);
    }
  }

  &.filled {
    background-color: var(--turquoise);
    border: 2px solid var(--turquoise);
    color: var(--white);

    &:hover,
    &:focus {
      background: var(--interactiveTurquoiseHover);
      border: 2px solid var(--interactiveTurquoiseHover);
    }

    &.active,
    &:active,
    &:active:hover {
      background: var(--interactiveTurquoisePress);
      border: 2px solid var(--interactiveTurquoisePress);
    }
  }

  &.transparent {
    background-color: transparent;
    border: 2px solid transparent;
    color: rgba(22, 22, 21, 1);

    &:hover,
    &:focus {
      background: var(--interactiveBlueHover);
      border: 2px solid interactiveBlueHover;
    }

    &.active,
    &:active,
    &:active:hover {
      background: var(--interactiveBluePress);
      border: 2px solid interactiveBluePress;
    }
  }

  /** Link **/
  &.link {
    color: #1890d3;
    background: transparent;
    padding: 0;
    text-transform: none;
    font-family: Lato;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 14px;
    gap: 6px;
    text-decoration: none;

    &.medium {
      font-size: 14px;
    }

    &.large {
      font-size: 16px;
    }

    &:hover,
    &:focus {
      background: transparent;
      text-decoration: underline;
    }

    &.active,
    &:active,
    &:active:hover {
      background: transparent;
      text-decoration: none;
    }

    i.fa.fa-circle-o-notch {
      color: #1890d3;
    }
  }

  &:disabled {
    opacity: 0.25;
  }

  i.fa.fa-circle-o-notch {
    display: none;
  }

  &.pending,
  &.pending.overlay {
    color: transparent;
    pointer-events: none;
  }

  &.pending i.fa.fa-circle-o-notch,
  &.pending.overlay i.fa.fa-circle-o-notch {
    position: absolute;
    display: block;
    font-size: 16px;
  }

  /** icon **/

  &.icon {
    background: transparent;
    color: rgba(22, 22, 21, 1);
    width: 40px;
    height: 40px;
    font-size: 18px;
    padding: 0px;

    &:hover,
    &:focus {
      background: rgba(24, 144, 211, 0.1);
    }

    &.active,
    &:active,
    &:active:hover {
      background: rgba(24, 144, 211, 0.25);
    }

    &.small {
      width: 40px;
      height: 40px;
      font-size: 12px;
    }

    &.large {
      width: 48px;
      height: 48px;
      font-size: 24px;
    }
  }
}
</style>